import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Inbox as InboxIcon, Mail, Paperclip, UserCheck, Trash2, Sparkles, Reply, Send, FileText, Contact } from "lucide-react";
import { downloadInboxVcard } from "@/lib/vcard";
import { InboxPdfToDossier } from "@/components/InboxPdfToDossier";

const TABS = [
  { v: "pending", l: "Openstaand" },
  { v: "assigned", l: "Toegewezen" },
  { v: "discarded", l: "Weggegooid" },
];

export default function Inbox() {
  const [tab, setTab] = useState("pending");
  const [msgs, setMsgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [picks, setPicks] = useState({});
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);


  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const rid = ++reqRef.current;
    setLoading(true);
    try {
      const r = await api.get(`/inbound?status=${tab}`);
      if (rid !== reqRef.current) return;
      setMsgs(r.data || []);
      const init = {};
      (r.data || []).forEach((m) => { if (m.suggested_tenant_id) init[m.id] = m.suggested_tenant_id; });
      setPicks((p) => ({ ...init, ...p }));
    } catch { toast.error("Postvak laden mislukt"); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
    api.get("/inbound/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const tenantOptions = tenants.map((t) => ({ value: t.id, label: `${t.first_name} ${t.last_name}${t.client_name ? ` · ${t.client_name}` : ""}` }));

  const assign = async (m) => {
    const tid = picks[m.id];
    if (!tid) { toast.error("Kies eerst een debiteur"); return; }
    try {
      await api.post(`/inbound/${m.id}/assign`, { tenant_id: tid });
      toast.success("Gekoppeld — toegevoegd aan de tijdslijn van de debiteur");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Koppelen mislukt"); }
  };
  const discard = async (m) => {
    try { await api.post(`/inbound/${m.id}/discard`); toast.success("Bericht weggegooid"); load(); }
    catch { toast.error("Actie mislukt"); }
  };
  const remove = async (m) => {
    try { await api.delete(`/inbound/${m.id}`); load(); } catch { toast.error("Verwijderen mislukt"); }
  };
  const simulate = async () => {
    try { await api.post("/inbound/test"); toast.success("Testbericht toegevoegd aan Postvak"); setTab("pending"); load(); }
    catch { toast.error("Testbericht mislukt"); }
  };
  const downloadContact = () => {
    if (!cfg?.inbound_email) { toast.error("Stel eerst het doorstuuradres in via Instellingen → Postvak"); return; }
    downloadInboxVcard(cfg.inbound_email);
  };

  const [reply, setReply] = useState(null); // { id, subject, body, attachments, letterIds }
  const [replyLetters, setReplyLetters] = useState([]);
  const [sending, setSending] = useState(false);
  const replyFileRef = useRef(null);
  const [upLoading, setUpLoading] = useState(false);
  useEffect(() => {
    const tid = reply && picks[reply.id];
    if (reply && tid) {
      api.get(`/tenants/${tid}/incasso-traject`).then((r) => setReplyLetters(r.data || [])).catch(() => setReplyLetters([]));
    } else {
      setReplyLetters([]);
    }
  }, [reply, picks]);
  const addReplyFile = async (file) => {
    if (!file) return;
    setUpLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReply((rp) => ({ ...rp, attachments: [...(rp.attachments || []), { filename: file.name, url: r.data.url }] }));
    } catch { toast.error("Uploaden mislukt"); }
    setUpLoading(false);
  };
  const sendReply = async (m) => {
    if (!reply?.subject?.trim() || !reply?.body?.trim()) { toast.error("Vul onderwerp en bericht in"); return; }
    setSending(true);
    try {
      const r = await api.post(`/inbound/${m.id}/reply`, { tenant_id: picks[m.id] || "", subject: reply.subject, body: reply.body, attachments: reply.attachments || [], letter_ids: reply.letterIds || [] });
      toast.success(r.data.logged ? "Antwoord verzonden en gelogd in de tijdslijn" : "Antwoord verzonden");
      setReply(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0F1E3D] flex items-center gap-2"><InboxIcon className="h-6 w-6 text-[#C9A227]" /> Postvak</h1>
          <p className="text-slate-500 text-sm">Doorgestuurde e-mails van debiteuren — koppel handmatig aan de juiste debiteur of gooi ze weg.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={downloadContact} variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" title="Download het doorstuuradres als contact voor Outlook" data-testid="inbox-vcard-button">
            <Contact className="h-4 w-4 mr-2" /> Contact voor Outlook
          </Button>
          <Button onClick={simulate} variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" data-testid="inbox-simulate-button">
            <Sparkles className="h-4 w-4 mr-2" /> Testbericht simuleren
          </Button>
        </div>
      </div>


      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`inbox-tab-${t.v}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.v ? "bg-[#0F1E3D] text-white shadow-[inset_0_-2px_0_0_#C9A227]" : "text-slate-600 hover:text-slate-900"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Laden…</div>
      ) : msgs.length === 0 ? (
        <Card className="p-12 text-center text-slate-400" data-testid="inbox-empty">
          <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
          {tab === "pending" ? "Geen berichten ter beoordeling. Gebruik 'Testbericht simuleren' om te proberen." : "Geen berichten."}
        </Card>
      ) : (
        <div className="space-y-3">
          {msgs.map((m) => (
            <Card key={m.id} className="p-5" data-testid={`inbox-msg-${m.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{m.subject || "(geen onderwerp)"}</div>
                  <div className="text-sm text-slate-500">
                    Van: <b className="text-slate-700">{m.from_name || m.from_email}</b>{m.from_name ? ` <${m.from_email}>` : ""} · {new Date(m.received_at).toLocaleString("nl-NL")}
                    {m.direction === "uitgaand" && <span className="ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 align-middle" data-testid={`inbox-outgoing-${m.id}`}>Uitgaand (CC)</span>}
                    {m.auto_assigned && <span className="ml-1 inline-block text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 align-middle" data-testid={`inbox-auto-${m.id}`}>Automatisch gekoppeld</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {m.provider === "test" && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Test</Badge>}
                  {m.attachments?.length > 0 && <Badge variant="outline" className="gap-1"><Paperclip className="h-3 w-3" /> {m.attachments.length}</Badge>}
                </div>
              </div>

              <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 border rounded-lg p-3 max-h-40 overflow-y-auto">{m.text || "(geen tekstinhoud)"}</p>

              {m.suggested_reason && m.status === "pending" && (
                <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid={`inbox-suggestion-${m.id}`}>
                  <Sparkles className="h-3.5 w-3.5 shrink-0" /> Suggestie: {m.suggested_tenant_name ? <><b>{m.suggested_tenant_name}</b> — </> : null}{m.suggested_reason}
                </div>
              )}

              {m.status === "assigned" ? (
                <p className="mt-3 text-sm text-emerald-700"><UserCheck className="h-4 w-4 inline mr-1.5 -mt-0.5" />Gekoppeld aan <b>{m.assigned_tenant_name || "debiteur"}</b> · toegevoegd aan de tijdslijn</p>
              ) : null}
              {m.status === "assigned" ? (
                <InboxPdfToDossier msg={m} />
              ) : m.status === "discarded" ? (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-slate-400">Weggegooid</span>
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" onClick={() => remove(m)} data-testid={`inbox-delete-${m.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Definitief verwijderen</Button>
                </div>
              ) : (
                <>
                <div className="mt-4 pt-3 border-t">
                {(m.suggested_candidates || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2 items-center" data-testid={`inbox-candidates-${m.id}`}>
                    <span className="text-xs text-slate-400">Kies debiteur:</span>
                    {m.suggested_candidates.map((c) => (
                      <button key={c.id} onClick={() => setPicks((p) => ({ ...p, [m.id]: c.id }))} data-testid={`inbox-candidate-${m.id}-${c.id}`}
                        className={`text-xs px-2.5 h-7 rounded-full border transition-colors ${picks[m.id] === c.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 hover:bg-slate-50 border-slate-200"}`}>{c.name}</button>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <Combobox testId={`inbox-assign-select-${m.id}`} value={picks[m.id] || ""} onChange={(v) => setPicks((p) => ({ ...p, [m.id]: v }))}
                      placeholder="Kies debiteur om aan te koppelen…" searchPlaceholder="Zoek debiteur…" options={tenantOptions} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => assign(m)} data-testid={`inbox-assign-button-${m.id}`}><UserCheck className="h-4 w-4 mr-1.5" /> Toewijzen</Button>
                    <Button size="sm" variant="outline" className="border-[#C9A227]/70 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => setReply({ id: m.id, subject: `Re: ${m.subject || ""}`, body: "", attachments: [], letterIds: [] })} data-testid={`inbox-reply-button-${m.id}`}><Reply className="h-4 w-4 mr-1.5" /> Beantwoorden</Button>
                    <Button size="sm" variant="outline" className="text-slate-500" onClick={() => discard(m)} data-testid={`inbox-discard-button-${m.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Weggooien</Button>
                  </div>
                </div>
                </div>
                {reply?.id === m.id && (
                  <div className="mt-3 border rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`inbox-reply-form-${m.id}`}>
                    <div className="text-xs text-slate-500">Antwoord aan <b>{m.from_email}</b></div>
                    <Input value={reply.subject} onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))} placeholder="Onderwerp" data-testid={`inbox-reply-subject-${m.id}`} />
                    <Textarea rows={5} value={reply.body} onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))} placeholder="Typ je antwoord…" data-testid={`inbox-reply-body-${m.id}`} />
                    <input ref={replyFileRef} type="file" className="hidden" data-testid={`inbox-reply-file-${m.id}`} onChange={(e) => addReplyFile(e.target.files?.[0])} />
                    <div className="flex flex-wrap items-center gap-2">
                      {(reply.attachments || []).map((a, ai) => (
                        <span key={ai} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border bg-white text-xs text-slate-700" data-testid={`inbox-reply-att-${m.id}-${ai}`}>
                          <Paperclip className="h-3 w-3 shrink-0" /> <span className="truncate max-w-[160px]">{a.filename}</span>
                          <button type="button" onClick={() => setReply((rp) => ({ ...rp, attachments: rp.attachments.filter((_, j) => j !== ai) }))} className="text-slate-400 hover:text-red-600 text-sm leading-none">×</button>
                        </span>
                      ))}
                      <Button size="sm" variant="outline" disabled={upLoading} onClick={() => replyFileRef.current?.click()} data-testid={`inbox-reply-attach-${m.id}`}><Paperclip className="h-3.5 w-3.5 mr-1.5" /> {upLoading ? "Uploaden…" : "Bestand toevoegen"}</Button>
                    </div>
                    {replyLetters.length > 0 && (
                      <div className="space-y-1 pt-1 border-t" data-testid={`inbox-reply-incasso-${m.id}`}>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 pt-2"><FileText className="h-3 w-3" /> Incassobrief als PDF bijvoegen:</div>
                        <div className="flex flex-wrap gap-1.5">
                          {replyLetters.map((l) => {
                            const on = (reply.letterIds || []).includes(l.id);
                            return (
                              <button key={l.id} type="button" data-testid={`inbox-reply-incasso-chip-${m.id}-${l.id}`}
                                onClick={() => setReply((rp) => ({ ...rp, letterIds: on ? (rp.letterIds || []).filter((x) => x !== l.id) : [...(rp.letterIds || []), l.id] }))}
                                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 hover:bg-slate-50 border-slate-200"}`}>
                                <FileText className="h-3 w-3 shrink-0" /> {l.kenmerk || l.subject}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={sending} className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => sendReply(m)} data-testid={`inbox-reply-send-${m.id}`}><Send className="h-4 w-4 mr-1.5" /> {sending ? "Versturen…" : "Versturen"}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setReply(null)}>Annuleren</Button>
                    </div>
                    <p className="text-[11px] text-slate-400">Kies eventueel eerst een debiteur hierboven; het antwoord wordt dan ook in diens tijdslijn gelogd.</p>
                  </div>
                )}
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
